import styles from 'LandingSite/layout/Terms/Terms.module.scss';
import React, {useEffect} from 'react';

/**
 * Public Terms & Conditions (acculex.io/terms). Keep in step with the canonical source in the
 * platform repo (docs/legal/TERMS_AND_CONDITIONS.md).
 */
const Terms = () => {

    useEffect(() => {
        window.scrollTo(0, 0)
    }, []);

    return (
        <div className={styles.terms}>
            <h1>Terms and Conditions</h1>
            <p><em>Last updated: 9 July 2026</em></p>

            <p>These Terms and Conditions ("Terms") are a legal agreement between you and <strong>Acculex</strong>
                (a service of Claritas Research Ltd — "Acculex", "we", "us", "our") governing your use of
                the Acculex platform at acculex.io, the Acculex ID app, and related services (together, the
                "Services"). By creating an account or using the Services you agree to these Terms. If you
                are agreeing on behalf of an organisation, you confirm you have authority to bind it, and
                "you" means that organisation. Please also read our <a href="/privacy">Privacy Policy</a>,
                which forms part of these Terms.</p>

            <h3>1. Professional-grade legal documents and guidance</h3>
            <p>Acculex gives you legal documents and guidance built to a professional standard. Our
                templates, clauses, workflows and guidance are created, curated and kept up to date by
                qualified legal professionals, and are designed to give you the same quality, rigour and
                confidence you would expect from instructing a lawyer — tailored to your jurisdiction and to
                your answers, and at a fraction of the cost and time. Our aim is to give you legal work
                product you can rely on.</p>
            <p>Acculex delivers this through technology rather than as a traditional, individually-retained
                law firm, so using the Services does not by itself create a bespoke retainer with a named
                individual lawyer. That does not lessen the quality of what you receive — it means
                professionally-prepared legal content is available to you on demand. Because only you know
                the full facts of your situation, we recommend you read what you produce and satisfy
                yourself that it fits your circumstances; and for an unusually complex, high-value,
                contentious or novel matter you may wish to obtain an opinion on your specific facts, which
                the Services are designed to help you do. You remain responsible for the decisions you take
                and for using each document for its intended purpose.</p>

            <h3>2. The Services</h3>
            <p>The Services may include guided workflows and document automation; electronic signature;
                identity verification (Acculex ID); an optional AI assistant; and account, storage and
                administration features. We may add, change, or remove features. Some features are optional
                or gated by your plan.</p>

            <h3>3. Eligibility and your account</h3>
            <p>You must be at least 18 and able to form a binding contract. You agree to provide accurate
                registration information, keep it up to date, keep your credentials confidential, enable
                available security features, and be responsible for activity under your account. Notify us
                promptly of any unauthorised use. We may refuse, suspend or reclaim accounts to protect the
                Services or comply with law.</p>

            <h3>4. Acceptable use</h3>
            <p>You must not: use the Services unlawfully or to infringe others' rights; upload malicious
                code; attempt to gain unauthorised access, probe, or disrupt the Services; scrape or harvest
                data except as permitted; reverse-engineer or copy the Services or create a competing
                product from them; misrepresent your identity or authority; use the Services to harass,
                defraud, or process personal data unlawfully; or exceed or circumvent plan limits, rate
                limits or usage controls. You must comply with all laws that apply to your use, including
                data-protection, export, sanctions and anti-money-laundering laws.</p>

            <h3>5. Your content and your responsibilities</h3>
            <p>You retain ownership of the information and materials you submit ("Your Content"). You grant
                us a worldwide, non-exclusive licence to host, process, transmit, display and adapt Your
                Content solely to provide and support the Services and as described in the Privacy Policy.
                You represent and warrant that you have the right to submit Your Content; that, where it
                includes personal information about other people, you have a lawful basis and any required
                consent to provide it and to have us process it; and that Your Content and your use of any
                output do not infringe any third party's rights or breach any law. Where we act as processor
                of personal data, you (or your organisation) are the controller and our data-processing
                terms apply.</p>

            <h3>6. Documents and outputs</h3>
            <p>Documents and other outputs are prepared to a professional standard from our lawyer-curated
                templates and rules applied to your answers, and are designed to be ready to use. Because
                only you know your full circumstances, we recommend you read each document and satisfy
                yourself that it fits your situation before you rely on it or sign it, and adapt it where
                your facts require. Subject to our rights in Section 11, the completed document you generate
                is yours.</p>

            <h3>7. Electronic signatures</h3>
            <p>Where you use the e-signature feature, you are responsible for ensuring that the document may
                lawfully be signed electronically in the relevant jurisdiction and that electronic signature
                is appropriate for it. The validity and admissibility of electronic signatures, and the
                categories of document that may not be signed electronically (for example certain wills,
                deeds, or documents needing notarisation or specific formalities), vary by jurisdiction. We
                provide tooling and a completion certificate (including signing metadata) to help evidence
                signature, but we do not guarantee that any given signature or document is valid,
                enforceable or admissible, and we are not a certification authority or a qualified
                trust-service provider unless expressly stated. You are responsible for identity checks and
                authority of signatories except to the extent you use our identity-verification feature.</p>

            <h3>8. Identity verification (Acculex ID)</h3>
            <p>If you use identity verification, you must have a lawful basis and, where required, the
                individual's explicit consent to collect and process their identity and biometric data, and
                you must use the results only for the stated, lawful purpose (for example a statutory
                right-to-work check). Verification results are indicative and are one factor in your
                decision — they are not a guarantee of a person's identity, eligibility or honesty, and
                where a result is inconclusive it is provided for human review. You are responsible for your
                decisions and for complying with the laws that apply to you (including anti-discrimination,
                employment and immigration law). Biometric data is handled as described in the Privacy
                Policy.</p>

            <h3>9. AI assistant</h3>
            <p>Any AI assistant helps you navigate the Services and get quick answers. It is a powerful tool
                and, like any tool, is best used with your own judgement; for anything you will rely on,
                confirm it against the relevant document or guidance. Do not submit more personal or
                confidential information to it than necessary.</p>

            <h3>10. Subscriptions, fees and billing</h3>
            <p>Some features require a paid subscription. Prices, plan limits, and any free tier are as
                shown in the Services at the time you subscribe. Paid subscriptions are billed through our
                payment processor (Stripe); by subscribing you authorise the recurring charge for the plan
                and interval you select. Unless stated otherwise: subscriptions renew automatically until
                cancelled; you may cancel at any time, effective at the end of the current period; fees are
                exclusive of taxes, which you are responsible for; and, except where required by law or
                expressly stated, fees are non-refundable for the period already started. We may change
                prices or plans on reasonable notice, effective from your next renewal. Free-tier usage is
                subject to the caps shown in the Services and may change. Failure to pay may lead to
                suspension or downgrade.</p>

            <h3>11. Intellectual property</h3>
            <p>The Services, including our software, templates, clause libraries, models, designs and brand,
                are owned by us or our licensors and are protected by intellectual-property laws. We grant
                you a limited, revocable, non-exclusive, non-transferable licence to use the Services in
                accordance with these Terms and your plan. You must not remove proprietary notices or use
                our brand without permission. If you give us feedback or suggestions, we may use them
                without obligation to you.</p>

            <h3>12. Third-party services</h3>
            <p>The Services integrate third-party services (for example Stripe for payments and our
                messaging and AI providers). Your use of those may be subject to the third party's own
                terms, and we are not responsible for third-party services except as expressly stated.</p>

            <h3>13. Service warranties</h3>
            <p>We provide the Services and content with reasonable care and skill, and we work to keep them
                accurate, current and available. To the extent permitted by law — and without affecting the
                statutory rights of consumers (Section 18) or any liability that cannot lawfully be excluded
                (Section 14) — the Services are otherwise provided without further warranties: we cannot
                guarantee a specific legal outcome, that the Services will be uninterrupted or error-free, or
                that a document will achieve a particular result in every circumstance.</p>

            <h3>14. Limitation of liability</h3>
            <p>Nothing in these Terms limits or excludes liability for death or personal injury caused by
                negligence; fraud or fraudulent misrepresentation; or any liability that cannot lawfully be
                limited or excluded (including certain consumer rights — see Section 18).</p>
            <p>Subject to that, to the fullest extent permitted by law: we are not liable for loss of
                profit, revenue, business, goodwill, anticipated savings, or for any indirect or
                consequential loss; and we are not liable for any loss arising from your reliance on an
                output, document or verification result, from your failure to obtain professional advice, or
                from Your Content. Our total aggregate liability arising out of or in connection with the
                Services and these Terms is limited to the greater of (a) the amount you paid us for the
                Services in the 12 months before the event giving rise to the claim, and (b) £100 (or
                local-currency equivalent). For consumers, these limits apply only so far as the law allows
                and do not affect your statutory rights (Section 18).</p>

            <h3>15. Indemnity (business users)</h3>
            <p>If you use the Services other than as a consumer, you will indemnify us against claims,
                losses and reasonable costs arising from your breach of these Terms, your misuse of the
                Services, Your Content, or your processing of others' personal data without a lawful basis.</p>

            <h3>16. Suspension and termination</h3>
            <p>You may stop using the Services and close your account at any time. We may suspend or
                terminate your access if you materially breach these Terms, fail to pay, or where necessary
                to protect the Services, other users, or comply with law — with notice where practicable. On
                termination, your licence ends and we will delete or return Your Content in accordance with
                the Privacy Policy and applicable law, except data we must retain. Terms that by their
                nature should survive (including Sections 1, 5–6, 11, 13–15, 18 and 20) survive termination.</p>

            <h3>17. Changes</h3>
            <p>We may change the Services and these Terms. We will post the updated Terms with a new "Last
                updated" date and, for material changes, give reasonable notice through the Services or by
                email. Continuing to use the Services after changes take effect means you accept them; if you
                do not agree, stop using the Services.</p>

            <h3>18. Consumers' statutory rights</h3>
            <p>If you use the Services as a consumer, you have statutory rights that these Terms do not
                exclude or limit, including:</p>
            <ul>
                <li><strong>United Kingdom</strong> — rights under the Consumer Rights Act 2015 (services
                    carried out with reasonable care and skill) and the Consumer Contracts Regulations 2013
                    (including cancellation rights for distance contracts, subject to the rules on digital
                    content/services you agree to start immediately);</li>
                <li><strong>European Union / EEA</strong> — mandatory consumer-protection and withdrawal
                    rights under applicable law;</li>
                <li><strong>Australia</strong> — the consumer guarantees under the Australian Consumer Law,
                    which cannot be excluded; where permitted, our liability for failure to meet a guarantee
                    is limited to re-supplying the services or paying the cost of re-supply;</li>
                <li><strong>New Zealand</strong> — the Consumer Guarantees Act 1993 and the Fair Trading
                    Act 1986 (the Consumer Guarantees Act does not apply where you acquire the Services for
                    business purposes and contracting out is permitted);</li>
                <li><strong>Canada</strong> — the consumer-protection legislation of your province or
                    territory (for example Ontario's Consumer Protection Act 2002, British Columbia's
                    Business Practices and Consumer Protection Act, or Québec's Consumer Protection Act and
                    its French-language requirements), which may provide non-excludable rights;</li>
                <li><strong>Singapore</strong> — the Consumer Protection (Fair Trading) Act 2003 and the
                    Sale of Goods Act, where applicable;</li>
                <li><strong>United States</strong> — any non-waivable consumer-protection rights under
                    applicable federal or state law (for example your state's unfair-or-deceptive-practices
                    statute);</li>
                <li><strong>Any other jurisdiction</strong> — any mandatory consumer-protection rights your
                    local law gives you, which we do not seek to exclude.</li>
            </ul>
            <p>Where any provision of these Terms conflicts with your non-excludable statutory rights, those
                rights prevail.</p>

            <h3>19. General</h3>
            <p>These Terms (with the Privacy Policy and any plan or order terms) are the entire agreement
                between us about the Services. If any provision is unenforceable, it is modified to the
                minimum extent necessary or severed, without affecting the rest. Our failure to enforce a
                term is not a waiver. You may not assign these Terms without our consent; we may assign them
                to a group company or a buyer of the business. We are not liable for delay or failure caused
                by events beyond our reasonable control. Except where a consumer's local law provides
                otherwise, a person who is not a party has no right to enforce these Terms. Notices to you
                may be given in the Services or by email.</p>

            <h3>20. Jurisdictions we serve, governing law and jurisdiction</h3>
            <p>The Services, and the legal content within them, are offered in and tailored to multiple
                jurisdictions, including the United Kingdom (England &amp; Wales, Scotland and Northern
                Ireland), the European Union / EEA, Australia, Canada, New Zealand, Singapore, and other
                countries. The jurisdictions available for a given document, and the law it is built to, are
                shown in the Services; you are responsible for selecting the jurisdiction that applies to
                you.</p>
            <p>Except where the mandatory law of your country (or, in Australia, your State or Territory; in
                Canada, your province or territory) of residence provides otherwise — which, for consumers,
                is preserved — these Terms and any dispute (including non-contractual disputes) are governed
                by the law of England and Wales, and the courts of England and Wales have exclusive
                jurisdiction. Save that if you are a consumer resident in the UK, the EEA, Australia, Canada,
                New Zealand, Singapore or another country whose law gives you the right to bring proceedings
                locally and to rely on your local law, you may do so and your local mandatory consumer law
                applies.</p>

            <h3>21. Contact</h3>
            <p>Acculex is a service of <strong>Claritas Research Ltd</strong>, a company registered in
                England and Wales (company number 10119402), registered office: 1 Aquarelle House,
                City Road, London, United Kingdom, EC1V 1AG.</p>
            <p><a href="mailto:support@acculex.io">support@acculex.io</a>
                {' '}(general) / <a href="mailto:privacy@acculex.io">privacy@acculex.io</a> (data
                protection).</p>
        </div>
    )
};

export default Terms;
